/** VUEX module for state management in app **/
import moment from 'moment';
import { combineCollection } from '@/utils/helpers.js';
import { ReportsApi } from '@/services/api-reports.js';

const DATE_FORMAT = 'YYYY-MM-DD';

// Local state -----------------------------------------------------
function defaultStateFactory() {
  return {
    all: {},
    report: {},
    statusTimerId: null,
    types: {
      dev: [
        { label: 'Emails', value: 'email', heading: 'emails' },
        { label: 'Mailboxes', value: 'mailbox', heading: 'mailboxes' },
        { label: 'API Clients', value: 'client', heading: 'API clients' },
        { label: 'Webhooks', value: 'webhook', heading: 'webhooks' },
      ],
      org: [
        { label: 'All Segments', value: 'all_segments', heading: 'segments' },
        { label: 'Flights', value: 'flight_spend', heading: 'flight segments' },
        { label: 'Hotels', value: 'hotel_spend', heading: 'hotel segments' },
        { label: 'Car', value: 'car_spend', heading: 'car segments' },
        { label: 'Rail', value: 'rail_spend', heading: 'rail segments' },
        {
          label: 'Ground Transportation',
          value: 'ground_spend',
          heading: 'ground transportation segments',
        },
        {
          label: 'Flight Origin-Destination',
          value: 'origin_destination',
          heading: 'flight routes',
        },
        { label: 'People', value: 'member', heading: 'people' },
        { label: 'Emails', value: 'email', heading: 'emails' },
      ],
    },

    filterSchemas: {
      dev: {
        default: _getDevFilterSchema(),
      },

      org: {
        default: _getDefaultOrgFilters(),
        all_segments: _getDefaultOrgFilters(),
        origin_destination: _getFlightFilters(),
        flight_spend: _getFlightFilters(),
        hotel_spend: _getHotelFilters(),
        car_spend: _getCarFilters(),
        rail_spend: _getRailFilters(),
        ground_spend: _getTransportationFilters(),
        member: _getMemberFilters(),
        email: _getEmailFilters(),
      },
    },
  };
}

export const state = defaultStateFactory();

// Computed properties of the state --------------------------------
export const getters = {
  all: (state) => state.all.data || [],
  pagination: (state) => state.all.meta || {},

  isReportProcessing: (state) => {
    return (
      state.report.attributes &&
      state.report.attributes.status &&
      state.report.attributes.status.toLowerCase() === 'processing'
    );
  },

  /**
   * Lookup the report type config based on the type value
   * @param {string} type - The report type value
   * @param {('org'|'dev')} [teamType='org'] - The current user's team type
   * @returns {(Object|undefined)}
   */
  reportConfigByType:
    (state) =>
    (type, teamType = 'org') => {
      return state.types[teamType].find((report) => report.value === type);
    },
};

// Synchronous mutation of the local state -------------------------
export const mutations = {
  UPDATE_REPORTS(state, data = {}) {
    state.all = data;
  },

  UPDATE_REPORT(state, report) {
    state.report = report;
  },

  UPDATE_STATUS_TIMER(state, timerId) {
    state.statusTimerId = timerId;
  },

  CLEAR_STATUS_TIMER(state) {
    clearTimeout(state.statusTimerId);
    state.statusTimerId = null;
  },

  RESET_STATE(state, defaultState) {
    state = defaultState;
  },
};

// Async CRUD actions against the api ------------------------------
export const actions = {
  /**
   * Fetch a list of all reports
   * @param {Object} context - vuex context object
   * @param {Object} payload
   * @param {Object} payload.query - route query object
   * @returns {Undefined}
   */
  async index({ commit, dispatch }, { query }) {
    try {
      const reports = await ReportsApi.index(query);
      commit('UPDATE_REPORTS', reports);
    } catch (err) {
      dispatch(
        'notifications/create',
        {
          type: 'error',
          message: 'We are unable fetch all your reports at this time . Please try again.',
        },
        { root: true }
      );
    }
  },

  /**
   * Create a new report
   * @param {Object} context - vuex context object
   * @param {Object} payload
   * @param {Object} payload.data - form data of the new report
   * @returns {Promise.<Object>}
   */
  store({ commit, state }, { data }) {
    return ReportsApi.store(data).then((report) => {
      commit('UPDATE_REPORT', report);
      return state.report;
    });
  },

  /**
   * Fetch a specific report by id
   * @param {Object} context - vuex context object
   * @param {Object} payload
   * @param {String} payload.id - report id
   * @param {Boolean} payload.forceFetch - Force a fech from the api even if we have the report cached
   * @returns {Promise.<Object>}
   */
  show({ commit, state, getters, dispatch }, { id, forceFetch }) {
    // 1. Have we cached a matching report already
    if (state.report.id === id && !forceFetch) {
      if (getters.isReportProcessing) {
        dispatch('pollReportStatus', { id: id });
      }
      return Promise.resolve(state.report);
    }

    // 2. or, Fetch the report from the api
    return ReportsApi.show(id).then((report) => {
      commit('UPDATE_REPORT', report);
      if (getters.isReportProcessing) {
        dispatch('pollReportStatus', { id: id });
      }

      return state.report;
    });
  },

  /**
   * Sets a timer to fetch the report again after 5 seconds;
   * @param {Object} context - vuex context object
   * @param {Object} payload
   * @param {String} id - report id
   * @returns {Promise}
   */
  pollReportStatus({ commit, dispatch }, { id }) {
    let timerId = setTimeout(function () {
      dispatch('show', { id: id, forceFetch: true });
    }, 5000);

    commit('UPDATE_STATUS_TIMER', timerId);
  },

  /**
   * Stop fetching a report and clear any timers
   * @param {Object} context - vuex context object
   * @returns {Undefined}
   */
  stopPolling({ commit, state }) {
    if (state.statusTimerId) {
      commit('CLEAR_STATUS_TIMER');
    }
  },

  /**
   * Sets the state back to the default values
   * @returns {Promise}
   */
  reset({ commit }) {
    commit('RESET_STATE', defaultStateFactory());
  },
};

/**
 * Return the filter config schema for developer report types
 * @param {string} type - currently all dev reports just get the date selector so type isn't used
 * @private
 * @returns {Object[]}
 */
function _getDevFilterSchema() {
  let schema = [
    {
      queryParam: ['start_date', 'end_date'],
      model: {},
      component: 'DateRange',
      name: 'Date Created',
      verb: '',
      persistent: false,
      optionalEnd: true,
    },
  ];
  return schema;
}

/**
 * Default filter config for all segment related org reports
 * @private
 * @returns {Object[]}
 */
function _getDefaultOrgFilters() {
  return [
    {
      queryParam: ['created_start_date', 'created_end_date'],
      model: {},
      component: 'DateRange',
      name: 'Date Added',
      verb: 'added',
      optionalEnd: true,
      presets: [
        {
          label: 'Added today',
          start: moment().startOf('day').format(DATE_FORMAT),
          end: moment().endOf('day').format(DATE_FORMAT),
        },
        {
          label: 'Added yesterday',
          start: moment().startOf('day').subtract(1, 'day').format(DATE_FORMAT),
          end: moment().endOf('day').subtract(1, 'day').format(DATE_FORMAT),
        },
        {
          label: 'Added last week',
          start: moment().startOf('day').subtract(7, 'days').format(DATE_FORMAT),
          end: moment().endOf('day').format(DATE_FORMAT),
        },
        {
          label: 'Added last month',
          start: moment().startOf('day').subtract(1, 'month').format(DATE_FORMAT),
          end: moment().endOf('day').format(DATE_FORMAT),
        },
      ],
    },

    {
      queryParam: ['start_date', 'end_date'],
      model: {},
      component: 'DateRange',
      name: 'Travel Date',
      verb: '',
      persistent: false,
      optionalEnd: true,
      presets: [
        {
          label: 'Starting today',
          start: moment().startOf('day').format(DATE_FORMAT),
          end: null,
        },

        {
          label: 'Traveling today',
          start: moment().startOf('day').format(DATE_FORMAT),
          end: moment().endOf('day').format(DATE_FORMAT),
        },
        {
          label: 'Traveling tomorrow',
          start: moment().startOf('day').add(1, 'day').format(DATE_FORMAT),
          end: moment().endOf('day').add(1, 'day').format(DATE_FORMAT),
        },
        {
          label: 'Traveling yesterday',
          start: moment().startOf('day').subtract(1, 'day').format(DATE_FORMAT),
          end: moment().endOf('day').subtract(1, 'day').format(DATE_FORMAT),
        },
        {
          label: 'Traveling last week',
          start: moment().startOf('day').subtract(7, 'days').format(DATE_FORMAT),
          end: moment().endOf('day').format(DATE_FORMAT),
        },
        {
          label: 'Traveling last month',
          start: moment().startOf('day').subtract(1, 'month').format(DATE_FORMAT),
          end: moment().endOf('day').format(DATE_FORMAT),
        },
      ],
    },

    {
      queryParam: 'source_class',
      model: '',
      component: 'RadioList',
      name: 'Booking Source Type',
      label: 'Booking Source Type',
      options: [
        { label: 'TMC Bookings', value: 'tmc', icon: 'tmc', color: 'text-brand-managed' },
        { label: 'Non-TMC Bookings', value: 'non-tmc', icon: 'non-tmc', color: 'text-brand-unmanaged' },
      ],
      verb: 'only',
    },

    {
      queryParam: 'source',
      model: [],
      component: 'AutocompleteList',
      apiMethod: 'getSuppliers',
      name: 'Booking Source',
      label: 'Booking Source',
      placeholder: '"Expedia"',
      verb: 'booked on',
    },

    {
      queryParam: 'not_source',
      model: [],
      component: 'AutocompleteList',
      apiMethod: 'getSuppliers',
      name: 'Booking Source to Exclude',
      label: 'Booking Source to Exclude',
      placeholder: '"Concur"',
      verb: 'no bookings from',
    },

    {
      queryParam: 'member_id',
      model: [],
      component: 'AutocompleteList',
      name: 'Booker',
      label: 'Booked By Name or Email',
      placeholder: '"Jane Doe" or "jane@acme"',
      verb: 'booked by',
      apiMethod: 'getMembers',
      selectedProp: 'id',
      showValue: true,
    },
  ];
}

/**
 * Filter config for flight reports
 * @private
 * @returns {Object[]}
 */
function _getFlightFilters() {
  let filters = [
    {
      queryParam: 'airline',
      model: [],
      component: 'SearchList',
      name: 'Airline',
      label: 'Airline',
      placeholder: '"American"',
      verb: 'on',
    },

    {
      queryParam: 'origin',
      model: [],
      component: 'AutocompleteList',
      name: 'Departure Airport',
      label: 'Departure airport name or code',
      placeholder: '"Dallas Fort Worth" or "DFW"',
      verb: 'from',
      apiMethod: 'getAirports',
      showValue: true,
    },

    {
      queryParam: 'destination',
      model: [],
      component: 'AutocompleteList',
      name: 'Arrival Airport',
      label: 'Arrival airport name or code',
      placeholder: '"Portland" or "PDX"',
      verb: 'to',
      apiMethod: 'getAirports',
      showValue: true,
    },
  ];

  return combineCollection(_getDefaultOrgFilters(), filters);
}

/**
 * Filter config for hotel reports
 * @private
 * @returns {Object[]}
 */
function _getHotelFilters() {
  let filters = [
    {
      queryParam: 'hotel_name',
      model: [],
      component: 'SearchList',
      name: 'Hotel Name',
      label: 'Hotel name includes',
      placeholder: '"Hilton Dallas Lincoln"',
      verb: 'at',
    },

    {
      queryParam: 'origin_city',
      model: [],
      component: 'AutocompleteList',
      name: 'City',
      label: 'City',
      placeholder: '"Shanghai, China"',
      verb: 'in',
      apiMethod: 'getLocations',
    },

    {
      queryParam: 'origin_country',
      model: [],
      component: 'AutocompleteList',
      name: 'Country',
      label: 'Country',
      placeholder: '"China"',
      verb: 'in',
      apiMethod: 'getCountries',
    },
  ];

  return combineCollection(_getDefaultOrgFilters(), filters);
}

/**
 * Filter config for car rental reports
 * @private
 * @returns {Object[]}
 */
function _getCarFilters() {
  let filters = [
    {
      queryParam: 'car_company',
      model: [],
      component: 'SearchList',
      name: 'Rental Agency',
      label: 'Rental Agency',
      placeholder: '"Hertz"',
      verb: 'with',
    },

    {
      queryParam: 'origin_city',
      model: [],
      component: 'AutocompleteList',
      name: 'Pick-up City',
      label: 'Pick-up City',
      placeholder: '"Riga, Latvia"',
      verb: 'picked up in',
      apiMethod: 'getLocations',
    },

    {
      queryParam: 'destination_city',
      model: [],
      component: 'AutocompleteList',
      name: 'Drop-off City',
      label: 'Drop-off City',
      placeholder: '"Tallin, Estonia"',
      verb: 'dropped off in',
      apiMethod: 'getLocations',
    },

    {
      queryParam: 'origin_country',
      model: [],
      component: 'AutocompleteList',
      name: 'Pick-up Country',
      label: 'Pick-up Country',
      placeholder: '"Lithuania"',
      verb: 'picked up in',
      apiMethod: 'getCountries',
    },

    {
      queryParam: 'destination_country',
      model: [],
      component: 'AutocompleteList',
      name: 'Drop-off Country',
      label: 'Drop-off Country',
      placeholder: '"Estonia"',
      verb: 'dropped off in',
      apiMethod: 'getCountries',
    },
  ];

  return combineCollection(_getDefaultOrgFilters(), filters);
}

/**
 * Filter config for rail reports
 * @private
 * @returns {Object[]}
 */
function _getRailFilters() {
  let filters = [
    {
      queryParam: 'rail_line',
      model: [],
      component: 'SearchList',
      name: 'Rail Line',
      label: 'Rail Line',
      placeholder: '"Deutsche Bahn"',
      verb: 'on',
    },

    {
      queryParam: 'train_number',
      model: [],
      name: 'Train Number',
      component: 'SearchList',
      label: 'Train number',
      placeholder: '"888"',
      verb: 'on Train Number',
    },

    {
      queryParam: 'origin',
      model: [],
      component: 'SearchList',
      name: 'Departure Station',
      label: 'Departure station name or code',
      placeholder: '"Berlin Hauptbahnhof"',
      verb: 'departing from',
    },

    {
      queryParam: 'destination',
      model: [],
      component: 'SearchList',
      name: 'Arrival Station',
      label: 'Arrival station name or code',
      placeholder: '"Paris Gare du Nord"',
      verb: 'arriving at',
    },

    {
      queryParam: 'origin_city',
      model: [],
      component: 'AutocompleteList',
      name: 'Departure City',
      label: 'Departure City',
      placeholder: '"Berlin, Germany"',
      verb: 'departing from',
      apiMethod: 'getLocations',
    },

    {
      queryParam: 'destination_city',
      model: [],
      component: 'AutocompleteList',
      name: 'Arrival City',
      label: 'Arrival City',
      placeholder: '"Paris, France"',
      verb: 'arriving in',
      apiMethod: 'getLocations',
    },

    {
      queryParam: 'origin_country',
      model: [],
      component: 'AutocompleteList',
      name: 'Departure Country',
      label: 'Departure Country',
      placeholder: '"Germany"',
      verb: 'departing from',
      apiMethod: 'getCountries',
    },

    {
      queryParam: 'destination_country',
      model: [],
      component: 'AutocompleteList',
      name: 'Arrival Country',
      label: 'Arrival Country',
      placeholder: '"France"',
      verb: 'arriving at',
      apiMethod: 'getLocations',
    },
  ];

  return combineCollection(_getDefaultOrgFilters(), filters);
}

/**
 * Filter config for ground transportation reports
 * @private
 * @returns {Object[]}
 */
function _getTransportationFilters() {
  let filters = [
    {
      queryParam: 'origin_city',
      model: [],
      component: 'AutocompleteList',
      name: 'Departure City',
      label: 'Departure City',
      placeholder: '"Berlin, Germany"',
      verb: 'departing from',
      apiMethod: 'getLocations',
    },

    {
      queryParam: 'destination_city',
      model: [],
      component: 'AutocompleteList',
      name: 'Arrival City',
      label: 'Arrival City',
      placeholder: '"Paris, France"',
      verb: 'arriving in',
      apiMethod: 'getLocations',
    },
  ];

  return combineCollection(_getDefaultOrgFilters(), filters);
}

/**
 * Filter config for member reports
 * @private
 * @returns {Object[]}
 */
function _getMemberFilters() {
  return [
    {
      queryParam: 'name',
      model: [],
      name: 'Name',
      component: 'SearchList',
      label: 'Name contains',
      placeholder: 'Jane',
      verb: 'named',
    },
    {
      queryParam: 'id',
      model: [],
      name: 'Email Address',
      component: 'AutocompleteList',
      label: 'Booked By Email Address',
      placeholder: 'jane@acme.com',
      apiMethod: 'getMembers',
      selectedProp: 'id',
      selectedDisplayProp: 'value',
      showValue: true,
      verb: 'at',
    },
    {
      queryParam: 'hometown',
      model: [],
      component: 'AutocompleteList',
      name: 'Hometown',
      label: 'Hometown name includes',
      placeholder: '"Dallas"',
      apiMethod: 'getLocations',
      verb: 'from',
    },
  ];
}

function _getEmailFilters() {
  return [
    {
      queryParam: ['start_date', 'end_date'],
      model: {},
      component: 'DateRange',
      name: 'Date Added',
      verb: '',
      persistent: false,
      optionalEnd: true,
    },
  ];
}
